// A replay in Vimeo's own player (@vimeo/player, bundled from npm, never a CDN), at the top of the
// replay's own page (Adam, 23 Oct). Until Play is pressed it's the board's dark box with its play
// button (FreeWebinars.dc.html lines 136 to 139), so nothing loads from Vimeo for someone who only
// looks. 1 press of Play makes the iframe from the replay's link, with its privacy hash (C30), and
// starts it where they left off; a chapter, once it plays, moves it there. onStart is told when it
// starts, so the page shows what goes with it only then. The Player is given the iframe, so it never
// asks Vimeo's oEmbed (the site's Content-Security-Policy allows only player.vimeo.com in a frame).
// Where they are is saved as they watch (vimeo.js), never while staff view as a member.
// Before Play the whole 16:9 box is 1 button, "Play the replay" (review of 23 Oct: a tap on the picture
// anywhere plays it, not only on the round disc, as the masterclass's cover does); the disc and the line
// under it are drawn inside it. Once Vimeo's frame is there, focus moves to it, so a keyboard user isn't
// left on the page's body when the button goes.
// Before Play, the webinar's own thumbnail (poster, a link sign-file signed) sits behind the play
// button where it has 1 (Adam, 9 Oct), with a solid near-black band behind "Press play" so the words
// keep 4.5 to 1 on any picture. The picture comes from the hub's storage, never Vimeo; its onError and
// onLoad go to free/thumbnails.js, as a card's do.
// The box is 16:9 at every width (free.css): before Play, while the thumbnail's link is being signed,
// with the thumbnail, and with Vimeo's player filling it, so pressing Play never moves the page.
import { useEffect, useId, useRef, useState } from 'react'
import Player from '@vimeo/player'
import * as f from '../lib/format.js'
import { vimeoSrc, positionOnPause, startAt, SAVE_EVERY_MS, WATCHED_SHARE } from './vimeo.js'
import { PlayIcon } from './Icons.jsx'

const NOT_READY = "This replay isn't ready to play yet."

export function ReplayPlayer({ title, vimeo, savedSeconds, seek, onPosition, onStart, canSave, poster = null, onPosterError, onPosterLoad }) {
  const [start, setStart] = useState(null) // null until Play: then the second it starts from
  const [notReady, setNotReady] = useState(false)
  const lineId = useId()
  const frame = useRef(null)
  const player = useRef(null)
  const save = useRef(null)
  save.current = canSave ? onPosition : null
  const started = useRef(null)
  started.current = onStart || null

  function play(chapterSeconds = null) {
    if (!vimeo) { setNotReady(true); return }
    setStart(startAt({ chapterSeconds, savedSeconds }))
  }

  // A chapter: start there, or move there if it's already playing
  const seekN = seek?.n
  useEffect(() => {
    if (seekN == null) return
    if (player.current) player.current.setCurrentTime(seek.seconds).then(() => player.current?.play()).catch(() => {})
    else play(seek.seconds)
  }, [seekN])

  const loaded = start != null && Boolean(vimeo)
  useEffect(() => { if (loaded) started.current?.() }, [loaded])
  useEffect(() => {
    if (!loaded || !frame.current) return
    const p = new Player(frame.current)
    player.current = p
    // the button that was pressed has gone: put focus on Vimeo's frame, not the page's body
    const active = document.activeElement
    if (!active || active === document.body) frame.current.focus({ preventScroll: true })
    let lastSave = Date.now()
    let lastSeconds = start
    let lastPut = start
    let pastWatched = false
    const put = seconds => {
      if (!save.current || seconds === lastPut) return
      lastPut = seconds
      save.current(seconds)
    }
    const onTime = e => {
      lastSeconds = Math.floor(e.seconds)
      if (!pastWatched && e.percent >= WATCHED_SHARE) {
        pastWatched = true
        lastSave = Date.now()
        put(lastSeconds)
        return
      }
      if (Date.now() - lastSave >= SAVE_EVERY_MS) { lastSave = Date.now(); put(lastSeconds) }
    }
    const onPause = e => { lastSeconds = positionOnPause(e.seconds, e.percent); put(lastSeconds) }
    const onEnd = () => { lastSeconds = 0; put(0) }
    const onLeave = () => put(lastSeconds)
    p.on('timeupdate', onTime)
    p.on('pause', onPause)
    p.on('ended', onEnd)
    window.addEventListener('pagehide', onLeave)
    return () => {
      window.removeEventListener('pagehide', onLeave)
      try { p.off('timeupdate', onTime); p.off('pause', onPause); p.off('ended', onEnd) } catch { /* the frame is going */ }
      // leaving the replay's page: keep the place it had reached
      put(lastSeconds)
      player.current = null
    }
  }, [loaded])

  if (loaded) {
    // #t= starts it at the right second without waiting for the player to answer
    const src = vimeoSrc(vimeo, { autoplay: true }) + (start > 0 ? `#t=${start}s` : '')
    return (
      <div className="fw-player fw-player--on">
        <iframe ref={frame} src={src} title={title} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
      </div>
    )
  }
  const paused = !notReady && savedSeconds > 0
  const line = notReady ? NOT_READY : paused ? `Paused at ${f.clock(savedSeconds)}` : 'Press play'
  return (
    <div className={poster ? 'fw-player fw-player--poster' : 'fw-player'}>
      {poster && <img key={poster} className="fw-poster" src={poster} alt="" decoding="async" onError={onPosterError} onLoad={onPosterLoad} />}
      <button type="button" className="fw-cover" aria-label="Play the replay" aria-describedby={paused ? lineId : undefined} onClick={() => play()}>
        <span className="fw-play" aria-hidden="true"><PlayIcon size={30} /></span>
        <span className="fw-play-line" id={lineId}>{line}</span>
      </button>
      {/* a button's own words aren't read out as they change, so "not ready" is said here too */}
      <span className="sr-only" role="status">{notReady ? NOT_READY : ''}</span>
    </div>
  )
}
